import type { Constituent, Customer, Organization, User } from "@/types"
import replaceSubdomain from "@/components/helpers/replaceSubdomain"
import {
  useCurrentConstituent,
  useCurrentUser,
  useCustomer,
  useIsLoggedIn,
  useShowAdminView,
} from "@/hooks/reduxHooks"
import { useAppSelector } from "@/store/store"
import LoadingWavelengthRadio from "icons/LoadingWavelengthRadio"
import PropTypes from "prop-types"
import { Navigate, useLocation } from "react-router-dom"

const propTypes = {
  redirectTo: PropTypes.string,
  customerAllowedIf: PropTypes.func,
  component: PropTypes.elementType.isRequired,
}
const defaultProps = {
  redirectTo: "/login",
  customerAllowedIf: null,
}

type PrivateRouteAllowedIf = (
  customer: Customer | null,
  currentUser: User | null,
  organizations: Organization[],
  showAdminView: boolean,
  currentConstituent: Constituent | null,
) => boolean

type PrivateRouteProps = {
  redirectTo?: string
  customerAllowedIf?: PrivateRouteAllowedIf
  component: React.ElementType
}

const PrivateRoute = ({
  redirectTo = "/login",
  customerAllowedIf,
  component: Component,
}: PrivateRouteProps) => {
  const location = useLocation()
  const customer = useCustomer()
  const currentUser = useCurrentUser()

  const loggedIn = useIsLoggedIn()
  const organizations = useAppSelector((state) => state.navigation.user.organizations)
  const currentConstituent = useCurrentConstituent()
  const showAdminView = useShowAdminView()

  const redirectLocation = {
    pathname: redirectTo,
    state: { from: location },
  }

  if (!loggedIn) {
    // @ts-expect-error WARN: check this
    window.desiredRoute = location.pathname
    return <Navigate to={redirectLocation.pathname} state={redirectLocation.state} replace />
  }

  if (!organizations) {
    return <LoadingWavelengthRadio />
  }

  if (organizations?.length === 1 && customer?.subdomain === "app") {
    // @ts-expect-error WARN: check this
    window.location = replaceSubdomain(organizations[0].subdomain)
    return <div />
  }

  if (
    customerAllowedIf &&
    !customerAllowedIf(customer, currentUser, organizations, showAdminView, currentConstituent)
  ) {
    return <Navigate to={redirectLocation.pathname} state={redirectLocation.state} replace />
  }

  return <Component />
}

PrivateRoute.propTypes = propTypes
PrivateRoute.defaultProps = defaultProps

export default PrivateRoute
